import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MetricDefinition {
  id: string;
  label: string;
  domain: 'email' | 'web' | 'loyalty';
  unit: '%' | 'count' | 'points';
  description: string;
  type: 'count' | 'ratio' | 'distinct_count' | 'sum_json';
}

export interface AnalyticsQueryInput {
  metric: string;
  dimensions?: string[];
  filters?: { tenant_id?: string };
  timeRange: { from: string; to: string };
  granularity: 'hour' | 'day' | 'week' | 'month';
  compareWith?: 'previous_period';
}

export interface SeriesPoint {
  date: string;
  value: number;
}

export interface AnalyticsQueryResult {
  series: SeriesPoint[];
  comparison?: SeriesPoint[];
  summary: {
    total: number;
    average: number;
    delta_pct?: number;
    delta_abs?: number;
  };
  meta: { query_execution_ms: number; scanned_mb: number };
}

// ─── Path helpers ─────────────────────────────────────────────────────────────

function metricsPath(mode: 'admin' | 'tenant') {
  return mode === 'admin' ? '/sa/analytics/metrics' : '/api/v1/analytics/metrics';
}

function queryPath(mode: 'admin' | 'tenant') {
  return mode === 'admin' ? '/sa/analytics/query' : '/api/v1/analytics/query';
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useMetrics(token: string, mode: 'admin' | 'tenant' = 'admin') {
  return useQuery<MetricDefinition[]>({
    queryKey: ['analytics-metrics', mode],
    queryFn: async () => {
      const res = await api.get<MetricDefinition[]>(metricsPath(mode));
      return res.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

export function useAnalyticsQuery(mode: 'admin' | 'tenant' = 'admin') {
  return useMutation<AnalyticsQueryResult, Error, AnalyticsQueryInput>({
    mutationFn: async (input) => {
      const res = await api.post<AnalyticsQueryResult>(queryPath(mode), input);
      return res.data;
    },
  });
}
